import { useMemo, useState } from "react";
import {
  Box,
  FormControl,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import ReactApexChart from "react-apexcharts";
import { numberFormatter } from "../../helper/Reports/common";
import {
  CAMPAIGN_SUMMARY_METRICS,
  useAmazonCampaignSummary,
} from "./dashboardHooks";
import SectionInfoIcon from "./SectionInfoIcon";

const PIE_COLORS = [
  "#2EC4B6",
  "#2C6BED",
  "#7B61FF",
  "#BEB1F9",
  "#64F3EB",
  "#1D216E",
  "#50A5B5",
  "#FF9800",
  "#70BDCC",
  "#7078CC",
];

const CURRENCY_METRICS = new Set(["spends", "revenue", "cpc", "cpm", "aov"]);

const TAB_OPTIONS = [
  { value: "vs", label: "Prospect V/S Retargeting" },
  { value: "prospect", label: "Prospect" },
  { value: "retarget", label: "Retargeting" },
];

const VS_LABEL_ORDER = ["Prospect", "Retargeting", "Others"];
const CHART_HEIGHT = 380;

function campaignFormatBucket(value) {
  const normalized = String(value ?? "").trim().toLowerCase();
  if (normalized === "prospect") return "Prospect";
  if (normalized === "retarget" || normalized === "retargeting") return "Retargeting";
  return "Others";
}

function formatMetricValue(metricKey, value) {
  if (value === null || value === undefined || value === "") return "-";
  return numberFormatter(
    value,
    CURRENCY_METRICS.has(metricKey),
    "number",
    2
  );
}

/**
 * @param {object} props
 * @param {string[]} [props.selectedPortfolios]
 * @param {object} [props.sx]
 */
export default function CampaignSummary({
  selectedPortfolios = [],
  sx,
  tab: tabProp,
  onTabChange,
  metricKey: metricKeyProp,
  onMetricKeyChange,
} = {}) {
  const [internalTab, setInternalTab] = useState("vs");
  const [internalMetricKey, setInternalMetricKey] = useState("spends");
  const tab = tabProp ?? internalTab;
  const metricKey = metricKeyProp ?? internalMetricKey;
  const setTab = (next) => {
    if (onTabChange) onTabChange(next);
    else setInternalTab(next);
  };
  const setMetricKey = (next) => {
    if (onMetricKeyChange) onMetricKeyChange(next);
    else setInternalMetricKey(next);
  };
  const { rows, loading } = useAmazonCampaignSummary(
    selectedPortfolios,
    tab,
    metricKey
  );

  const dimensionKey = tab === "vs" ? "campaign_format" : "targeting";

  const chartSlices = useMemo(() => {
    if (tab === "vs") {
      const totals = new Map();
      (rows || []).forEach((row) => {
        const label = campaignFormatBucket(row?.[dimensionKey]);
        const value = Number(row?.[metricKey] ?? 0);
        const safeValue = Number.isFinite(value) ? value : 0;
        totals.set(label, (totals.get(label) || 0) + safeValue);
      });
      return VS_LABEL_ORDER.map((label) => ({
        label,
        value: totals.get(label) || 0,
      })).filter((slice) => slice.value > 0);
    }

    return (rows || [])
      .map((row) => {
        const value = Number(row?.[metricKey] ?? 0);
        return {
          label: row?.[dimensionKey] || "Unknown",
          value: Number.isFinite(value) ? value : 0,
        };
      })
      .filter((slice) => slice.value > 0);
  }, [rows, tab, dimensionKey, metricKey]);

  const series = chartSlices.map((slice) => slice.value);
  const labels = chartSlices.map((slice) => slice.label);

  const options = useMemo(
    () => ({
      chart: {
        fontFamily: "Inter, sans-serif",
        toolbar: { show: false },
        parentHeightOffset: 0,
      },
      plotOptions: {
        pie: {
          customScale: 1.08,
          expandOnClick: false,
          offsetY: 8,
        },
      },
      labels,
      colors: PIE_COLORS,
      legend: { show: false },
      dataLabels: {
        enabled: true,
        style: {
          colors: ["#fff"],
          fontWeight: 400,
          fontSize: "12px",
        },
        dropShadow: { enabled: false },
      },
      stroke: {
        colors: ["#fff"],
        width: 2,
      },
      tooltip: {
        y: {
          formatter: (value) => formatMetricValue(metricKey, value),
        },
      },
      noData: { text: "No data to display" },
      states: {
        hover: {
          filter: { type: "none" },
        },
      },
    }),
    [labels, metricKey]
  );

  const handleTabChange = (_, nextTab) => {
    if (nextTab !== null) setTab(nextTab);
  };

  return (
    <Box sx={{ mb: 4, height: "100%", display: "flex", flexDirection: "column", ...sx }}>
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
          <Typography variant="h6" fontWeight={600} sx={{ fontSize: "16px" }}>
            Campaign Summary
          </Typography>
          <SectionInfoIcon
            lines={[
              "Pie of the selected base metric for the selected dates and portfolios.",
              "Prospect vs Retargeting groups campaign format into Prospect, Retargeting, and Others.",
              "Prospect and Retargeting tabs split that format by targeting.",
              "Spends, Impressions, Clicks, Revenue, and Orders are totals for the group.",
              "CTR = (Clicks / Impressions) × 100",
              "CVR = (Orders / Clicks) × 100",
              "CPM = (Spends / Impressions) × 1000",
              "AOV = Revenue / Orders",
              "CPC = Spends / Clicks",
              "ROAS = Revenue / Spends",
              "ACOS = (Spends / Revenue) × 100",
            ]}
          />
        </Box>
        <Typography variant="body2" color="text.secondary">
          Monitor performance insights to spot growth opportunities.
        </Typography>
      </Box>

      <Paper
        sx={{
          p: 2,
          borderRadius: 2,
          boxShadow: "none",
          border: "1px solid #E0E0E0",
          flex: 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <ToggleButtonGroup
          value={tab}
          exclusive
          onChange={handleTabChange}
          aria-label="campaign summary view"
          sx={{
            mb: 2,
            flexWrap: "wrap",
            gap: 1,
            "& .MuiToggleButtonGroup-grouped": {
              margin: 0,
              border: "1px solid #E0E0E0 !important",
              borderRadius: "8px !important",
            },
            "& .MuiToggleButton-root": {
              px: 1.5,
              py: 0.6,
              textTransform: "none",
              fontWeight: 600,
              fontSize: "12px",
              color: "text.secondary",
              "&.Mui-selected": {
                bgcolor: "rgba(97, 72, 246, 0.08)",
                color: "primary.main",
                borderColor: "primary.main !important",
                zIndex: 1,
                "&:hover": {
                  bgcolor: "rgba(97, 72, 246, 0.12)",
                },
              },
            },
          }}
        >
          {TAB_OPTIONS.map((option) => (
            <ToggleButton key={option.value} size="small" value={option.value}>
              {option.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            bgcolor: "rgb(234, 230, 253)",
            borderRadius: 1,
            px: 2,
            py: 0.75,
            mb: 2,
            gap: 2,
          }}
        >
          <Typography sx={{ fontSize: "13px", fontWeight: 600 }}>
            Base Metric
          </Typography>
          <FormControl size="small" variant="standard" sx={{ minWidth: 120 }}>
            <Select
              value={metricKey}
              disableUnderline
              onChange={(event) => setMetricKey(event.target.value)}
              sx={{
                fontSize: "13px",
                fontWeight: 600,
                "& .MuiSelect-select": { py: 0.5 },
              }}
            >
              {CAMPAIGN_SUMMARY_METRICS.map((metric) => (
                <MenuItem key={metric.key} value={metric.key}>
                  {metric.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 360,
          }}
        >
          {loading ? (
            <Box
              sx={{
                width: "100%",
                height: CHART_HEIGHT,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Skeleton
                variant="circular"
                sx={{
                  width: `min(100%, ${CHART_HEIGHT}px)`,
                  aspectRatio: "1 / 1",
                  height: "auto",
                }}
              />
            </Box>
          ) : series.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No campaign summary data for the selected period
            </Typography>
          ) : (
            <Box sx={{ width: "100%" }}>
              <ReactApexChart
                key={`${tab}-${metricKey}`}
                type="pie"
                height={CHART_HEIGHT}
                series={series}
                options={options}
              />
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: "center",
                  gap: 2,
                  mt: 1,
                }}
              >
                {labels.map((label, index) => (
                  <Box
                    key={`${label}-${index}`}
                    sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        bgcolor: PIE_COLORS[index % PIE_COLORS.length],
                        flexShrink: 0,
                      }}
                    />
                    <Typography sx={{ fontSize: "12px", color: "text.secondary" }}>
                      {label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
}
